import {
  AnalyticsEvents,
  useAnalyticsSafe
} from "./chunk-MXQ2GIJ4.js";
import {
  queryClient,
  tsr
} from "./chunk-N67WXNDR.js";
import {
  logger
} from "./chunk-4R6JFI7D.js";

// src/auth/token-manager.ts
var config = {
  storageType: "session",
  accessTokenKey: "flote_auth_token",
  refreshTokenKey: "flote_refresh_token"
};
var inMemoryAccessToken = null;
var inMemoryRefreshToken = null;
var storageListenerInitialized = false;
function getStorage() {
  if (typeof window === "undefined") return null;
  return config.storageType === "local" ? window.localStorage : window.sessionStorage;
}
function initStorageListener() {
  if (typeof window === "undefined" || storageListenerInitialized) return;
  window.addEventListener("storage", (event) => {
    if (config.storageType !== "local") return;
    if (event.key === config.accessTokenKey) {
      inMemoryAccessToken = event.newValue;
    } else if (event.key === config.refreshTokenKey) {
      inMemoryRefreshToken = event.newValue;
    }
  });
  storageListenerInitialized = true;
}
var tokenManager = {
  /**
   * Configure the token manager storage type.
   * Call this before any other tokenManager methods.
   * Automatically initializes cross-tab sync for localStorage mode.
   *
   * @param options - Configuration options
   * @param options.storageType - 'session' (default) or 'local' for persistent auth
   */
  configure: (options) => {
    if (options.storageType) {
      config.storageType = options.storageType;
    }
    inMemoryAccessToken = null;
    inMemoryRefreshToken = null;
    if (config.storageType === "local") {
      initStorageListener();
    }
  },
  /**
   * Get current storage type configuration
   */
  getStorageType: () => config.storageType,
  /**
   * Retrieves the access token (JWT).
   * Checks in-memory cache first, then falls back to configured storage.
   */
  getToken: () => {
    if (inMemoryAccessToken) {
      return inMemoryAccessToken;
    }
    try {
      const storage = getStorage();
      if (storage) {
        const tokenFromStorage = storage.getItem(config.accessTokenKey);
        if (tokenFromStorage) {
          inMemoryAccessToken = tokenFromStorage;
          return tokenFromStorage;
        }
      }
    } catch (error) {
      console.error("Could not access storage for access token.", error);
    }
    return null;
  },
  /**
   * Stores the access token (JWT) in memory and configured storage.
   */
  setToken: (token) => {
    inMemoryAccessToken = token;
    try {
      const storage = getStorage();
      if (storage) {
        storage.setItem(config.accessTokenKey, token);
      }
    } catch (error) {
      console.error("Could not set access token in storage.", error);
    }
  },
  /**
   * Retrieves the refresh token.
   * Checks in-memory cache first, then falls back to configured storage.
   */
  getRefreshToken: () => {
    if (inMemoryRefreshToken) {
      return inMemoryRefreshToken;
    }
    try {
      const storage = getStorage();
      if (storage) {
        const tokenFromStorage = storage.getItem(config.refreshTokenKey);
        if (tokenFromStorage) {
          inMemoryRefreshToken = tokenFromStorage;
          return tokenFromStorage;
        }
      }
    } catch (error) {
      console.error("Could not access storage for refresh token.", error);
    }
    return null;
  },
  /**
   * Stores the refresh token in memory and configured storage.
   */
  setRefreshToken: (token) => {
    inMemoryRefreshToken = token;
    try {
      const storage = getStorage();
      if (storage) {
        storage.setItem(config.refreshTokenKey, token);
      }
    } catch (error) {
      console.error("Could not set refresh token in storage.", error);
    }
  },
  /**
   * Stores both access and refresh tokens at once.
   * Convenience method for login flows.
   */
  setTokens: (accessToken, refreshToken) => {
    tokenManager.setToken(accessToken);
    tokenManager.setRefreshToken(refreshToken);
  },
  /**
   * Clears the access token from memory and storage.
   */
  clearToken: () => {
    inMemoryAccessToken = null;
    try {
      const storage = getStorage();
      if (storage) {
        storage.removeItem(config.accessTokenKey);
      }
    } catch (error) {
      console.error("Could not clear access token from storage.", error);
    }
  },
  /**
   * Clears the refresh token from memory and storage.
   */
  clearRefreshToken: () => {
    inMemoryRefreshToken = null;
    try {
      const storage = getStorage();
      if (storage) {
        storage.removeItem(config.refreshTokenKey);
      }
    } catch (error) {
      console.error("Could not clear refresh token from storage.", error);
    }
  },
  /**
   * Clears all tokens (access and refresh) from memory and storage.
   * Use this for logout flows.
   */
  clearAllTokens: () => {
    tokenManager.clearToken();
    tokenManager.clearRefreshToken();
  },
  /**
   * Check if a refresh token is available.
   * Useful for determining if token refresh is possible.
   */
  hasRefreshToken: () => {
    return tokenManager.getRefreshToken() !== null;
  }
};

// src/auth/auth-hooks.ts
import { useEffect, useRef, useCallback, useState } from "react";
var authLogger = logger.child({ component: "auth-hooks" });
var refreshPromise = null;
var lastRefreshAttempt = 0;
var REFRESH_DEBOUNCE_MS = 5e3;
function isTokenExpired(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3 || !parts[1]) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp * 1e3;
    const now = Date.now();
    return now >= exp;
  } catch (error) {
    authLogger.warn("Error decoding token for expiration check", {
      error: error instanceof Error ? error.message : "Unknown error"
    });
    return true;
  }
}
function isTokenExpiringSoon(token, thresholdMinutes = 5) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3 || !parts[1]) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp * 1e3;
    const now = Date.now();
    const threshold = thresholdMinutes * 60 * 1e3;
    return exp - now < threshold;
  } catch (error) {
    authLogger.warn("Error decoding token for expiration check", {
      error: error instanceof Error ? error.message : "Unknown error"
    });
    return true;
  }
}
function needsInitialRefresh() {
  const accessToken = tokenManager.getToken();
  const refreshToken = tokenManager.getRefreshToken();
  if (!accessToken || !refreshToken) {
    authLogger.debug("Initial refresh check - no tokens", {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken
    });
    return false;
  }
  const tokenExpired = isTokenExpired(accessToken);
  const tokenExpiringSoon = isTokenExpiringSoon(accessToken, 5);
  const shouldRefresh = tokenExpired || tokenExpiringSoon;
  authLogger.debug("Initial refresh check", {
    hasAccessToken: true,
    hasRefreshToken: true,
    tokenExpired,
    tokenExpiringSoon,
    shouldRefresh
  });
  return shouldRefresh;
}
var useVerifyEmail = () => {
  return tsr.auth.verifyEmail.useMutation();
};
var verifyCustomerAccount = async (email) => {
  const res = await tsr.customers.getByEmail.query({ query: { email } });
  return res;
};
var useVerifyOTP = () => {
  return tsr.auth.verifyOtp.useMutation();
};
var useLogout = () => {
  return tsr.auth.logout.useMutation();
};
var useRefreshToken = () => {
  return tsr.auth.refresh.useMutation();
};
var useCreateB2BWaveCustomer = () => {
  return tsr.customers.create.useMutation();
};
var useMe = () => {
  const refreshIntervalRef = useRef(null);
  const refreshTokenMutation = useRefreshToken();
  const isRefreshingRef = useRef(false);
  const mutateAsyncRef = useRef(refreshTokenMutation.mutateAsync);
  useEffect(() => {
    mutateAsyncRef.current = refreshTokenMutation.mutateAsync;
  });
  const hasAttemptedInitialRefresh = useRef(false);
  const [initialCheckComplete, setInitialCheckComplete] = useState(() => {
    const needsRefresh = needsInitialRefresh();
    authLogger.debug("useMe mount - initial state", {
      needsRefresh,
      initialCheckComplete: !needsRefresh
    });
    return !needsRefresh;
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const attemptRefreshWithRefreshToken = useCallback(async () => {
    const now = Date.now();
    if (now - lastRefreshAttempt < REFRESH_DEBOUNCE_MS) {
      authLogger.debug("Refresh debounced, too soon after last attempt");
      if (refreshPromise) {
        return refreshPromise;
      }
      return false;
    }
    if (refreshPromise) {
      authLogger.debug("Refresh already in progress, waiting...");
      return refreshPromise;
    }
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      authLogger.debug("No refresh token available");
      return false;
    }
    lastRefreshAttempt = now;
    refreshPromise = (async () => {
      try {
        authLogger.info("Attempting token refresh with refresh token");
        const result = await mutateAsyncRef.current({
          body: { refreshToken }
        });
        if (result.body?.success && result.body.jwt) {
          tokenManager.setToken(result.body.jwt);
          if (result.body.refreshToken) {
            tokenManager.setRefreshToken(result.body.refreshToken);
          }
          authLogger.info("Token refreshed successfully via refresh token");
          return true;
        }
        authLogger.warn("Refresh token response missing JWT");
        return false;
      } catch (error) {
        authLogger.warn("Refresh with refresh token failed", {
          error: error instanceof Error ? error.message : "Unknown error"
        });
        tokenManager.clearAllTokens();
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  }, []);
  useEffect(() => {
    if (initialCheckComplete || hasAttemptedInitialRefresh.current) {
      authLogger.debug("Initial check already complete or attempted, skipping refresh");
      return;
    }
    hasAttemptedInitialRefresh.current = true;
    const doInitialRefresh = async () => {
      authLogger.info("Starting initial token refresh before query runs");
      const refreshed = await attemptRefreshWithRefreshToken();
      authLogger.info("Initial token refresh completed", { success: refreshed });
      setInitialCheckComplete(true);
    };
    doInitialRefresh();
  }, [initialCheckComplete]);
  authLogger.debug("Query config", { enabled: initialCheckComplete });
  const query = tsr.auth.me.useQuery({
    queryKey: ["auth-me"],
    // Don't run query until initial refresh check is complete
    enabled: initialCheckComplete,
    retry: (failureCount, error) => {
      if (error && typeof error === "object") {
        const status = error.status;
        const responseStatus = error.body?.status;
        const resolvedStatus = status ?? responseStatus;
        if (resolvedStatus === 401 || resolvedStatus === 403) {
          authLogger.debug("Auth failure detected, not retrying", {
            status: resolvedStatus,
            failureCount
          });
          return false;
        }
      }
      return failureCount < 1;
    },
    staleTime: 30 * 1e3,
    gcTime: 2 * 60 * 1e3,
    refetchOnReconnect: true,
    refetchOnMount: true,
    // Changed from 'always' - only refetch if data is stale
    refetchInterval: false,
    refetchOnWindowFocus: false
  });
  useEffect(() => {
    if (initialCheckComplete && !query.isLoading && !isInitialized) {
      authLogger.debug("Auth initialization complete", {
        hasCustomer: !!query.data?.body,
        hasError: !!query.error
      });
      setIsInitialized(true);
    }
  }, [initialCheckComplete, query.isLoading, query.data?.body, query.error, isInitialized]);
  const isAuthenticatedRef = useRef(false);
  useEffect(() => {
    isAuthenticatedRef.current = !!query.data?.body;
  }, [query.data?.body]);
  useEffect(() => {
    const checkTokenExpiration = async () => {
      if (!isAuthenticatedRef.current || isRefreshingRef.current) return;
      const token = tokenManager.getToken();
      if (!token) return;
      if (isTokenExpiringSoon(token, 10)) {
        authLogger.info("Token expiring soon, attempting refresh");
        const refreshed = await attemptRefreshWithRefreshToken();
        if (refreshed) {
          authLogger.info("Periodic token refresh successful, refetching user data");
          query.refetch();
        }
      }
    };
    refreshIntervalRef.current = setInterval(checkTokenExpiration, 1 * 60 * 1e3);
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [attemptRefreshWithRefreshToken, query.refetch]);
  return { ...query, isInitialized };
};
function useAuth() {
  const { data, isLoading, error, refetch, isInitialized } = useMe();
  const customer = data?.body ?? null;
  return {
    customer,
    isAuthenticated: !!customer,
    isLoading,
    isInitialized,
    error,
    refetch
  };
}

// src/auth/auth-context.tsx
import { createContext } from "react";
import { jsx } from "react/jsx-runtime";
var AuthContext = createContext(void 0);
var AuthProvider = ({ children }) => {
  const { customer, isAuthenticated, isLoading, isInitialized, error, refetch } = useAuth();
  const verifyEmailMutation = useVerifyEmail();
  const verifyOTPMutation = useVerifyOTP();
  const logoutMutation = useLogout();
  const createCustomerMutation = useCreateB2BWaveCustomer();
  const { track, identify, reset: resetAnalytics } = useAnalyticsSafe();
  const verifyEmail = async (email) => {
    track(AnalyticsEvents.SIGN_IN_STARTED, { email });
    return verifyEmailMutation.mutateAsync({ body: { email } });
  };
  const verifyOTP = async (email, otp) => {
    const result = await verifyOTPMutation.mutateAsync({ body: { email, otp } });
    const loginResponse = result.body;
    if (loginResponse?.success && loginResponse.jwt) {
      tokenManager.setToken(loginResponse.jwt);
      if (loginResponse.refreshToken) {
        tokenManager.setRefreshToken(loginResponse.refreshToken);
      }
      await refetch();
      if (loginResponse.customer) {
        const c = loginResponse.customer;
        identify({
          id: String(c.id),
          email: c.email ?? void 0,
          companyId: c.companyNumber ?? void 0,
          companyName: c.companyName ?? void 0,
          customerId: c.id
        });
        track(AnalyticsEvents.SIGN_IN_COMPLETED, {
          userId: String(c.id),
          email: c.email ?? "",
          companyId: c.companyNumber,
          companyName: c.companyName
        });
      }
    }
    return result;
  };
  const logout = async () => {
    try {
      track(AnalyticsEvents.SIGN_OUT, {});
      await logoutMutation.mutateAsync({ body: {} });
    } catch (error2) {
      console.error("Logout mutation failed:", error2);
    } finally {
      tokenManager.clearAllTokens();
      queryClient.setQueryData(["auth-me"], null);
      await queryClient.invalidateQueries({ queryKey: ["auth-me"] });
      resetAnalytics();
    }
  };
  const createCustomer = async (input) => {
    try {
      const result = await createCustomerMutation.mutateAsync({ body: input });
      return result;
    } catch (error2) {
      console.error("Create customer error:", error2);
      throw error2;
    }
  };
  return /* @__PURE__ */ jsx(
    AuthContext.Provider,
    {
      value: {
        customer,
        isAuthenticated,
        isLoading,
        isInitialized,
        error,
        verifyEmail,
        verifyOTP,
        logout,
        refetch,
        createCustomer
      },
      children
    }
  );
};

// src/auth/sms-hooks.ts
var smsLogger = logger.child({ component: "sms-hooks" });
var useSendSmsVerification = () => {
  return tsr.auth.sendSmsOtp.useMutation({
    onSuccess: (result) => {
      if (result.status === 200 && result.body.success) {
        smsLogger.info("SMS verification code sent successfully", {
          message: result.body.message
        });
      }
    },
    onError: (error) => {
      smsLogger.error("Failed to send SMS verification code", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
    }
  });
};
var useVerifySmsCode = () => {
  return tsr.auth.verifySmsOtp.useMutation({
    onSuccess: (result) => {
      if (result.status === 200 && result.body.success) {
        smsLogger.info("SMS code verification completed", {
          message: result.body.message,
          customerId: result.body.customer?.id
        });
      }
    },
    onError: (error) => {
      smsLogger.error("Failed to verify SMS code", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
    }
  });
};

// src/auth/index.ts
import { useContext } from "react";
var useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === void 0) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};

export {
  tokenManager,
  useVerifyEmail,
  verifyCustomerAccount,
  useVerifyOTP,
  useLogout,
  useRefreshToken,
  useCreateB2BWaveCustomer,
  useMe,
  useAuth,
  AuthContext,
  AuthProvider,
  useSendSmsVerification,
  useVerifySmsCode,
  useAuthContext
};
